import { DOCUMENT } from "@angular/common";
import { Inject, Injectable, LOCALE_ID, Renderer2, RendererFactory2 } from "@angular/core";
import { Meta, Title } from "@angular/platform-browser";
import { Client, Job, Page } from "@interfaces";
import { environment } from "src/environments/environment";
import { SubdomainService } from "../rest/subdomain.service";

@Injectable({
    providedIn: "root",
})
export class MetadataService {
    private renderer: Renderer2;

    // Constants for image dimensions
    private readonly DEFAULT_IMAGE_WIDTH = 500;
    private readonly DEFAULT_IMAGE_HEIGHT = 260;
    private readonly LARGE_IMAGE_WIDTH = 1200;
    private readonly LARGE_IMAGE_HEIGHT = 600;

    // Constants for Cloudinary
    private readonly CLOUDINARY_BASE_URL = 'https://res.cloudinary.com/minirobot/image/upload';
    private readonly DEFAULT_IMAGE_PATH = 'voyse/assets/meta-image';

    constructor(
        private title: Title,
        private meta: Meta,
        private subdomain: SubdomainService,
        private rendererFactory: RendererFactory2,
        @Inject(DOCUMENT) private document: Document,
        @Inject(LOCALE_ID) private locale: string
    ) {
        this.renderer = this.rendererFactory.createRenderer(null, null);
    }

    public setupJobMetaTags(job: Job) {
        this.subdomain.getClient().subscribe((subdomain: Client) => {
            const formattedTitle = this.formatJobTitle(job, subdomain);
            this.title.setTitle(formattedTitle);
			this.updateCanonicalUrl(job.permalink);

            // Add self-referential hreflang tag for the job's language
            if (job.lang) {
                this.addJobHrefLangTag(job, job.lang, subdomain);
            }

            const description = this.getDefaultDescription(job?.metaDescription, subdomain);

            this.meta.addTags([
                { name: "description", property: "description", content: description },
                { name: "title", property: "og:title", content: formattedTitle },
                { property: "og:type", content: "article" },
                { property: "og:description", content: description },
                { property: "og:url", content: job.permalink },
                { property: "article:published_time", content: job?.createdAt.toString() },
                { property: "article:modified_time", content: job?.updatedAt.toString() },
            ]);

            if (subdomain.integrations?.searchconsole?.apiKey) {
                this.meta.addTags([
                    { name: "google-site-verification", content: subdomain.integrations.searchconsole.apiKey },
                ]);
            }

            this.meta.addTags(this.getJobImageMetaTags(job, subdomain));
        });
    }

    updateCanonicalUrl(url: string) {
        const head = this.document.getElementsByTagName("head")[0];
        var element: HTMLLinkElement = this.document.querySelector(`link[rel='canonical']`) || null;
        if (element == null) {
            element = this.renderer.createElement("link") as HTMLLinkElement;
            this.renderer.appendChild(head, element);
        }
        this.renderer.setAttribute(element, "rel", "canonical");
        this.renderer.setAttribute(element, "href", url);
    }

    public preloadImage(url: string) {
        const head = this.document.getElementsByTagName("head")[0];
        var element: HTMLLinkElement = this.document.querySelector(`link[rel='preload']`) || null;
        if (element == null) {
            element = this.renderer.createElement("link") as HTMLLinkElement;
            this.renderer.appendChild(head, element);
        }
        this.renderer.setAttribute(element, "as", "image");
        this.renderer.setAttribute(element, "rel", "preload");
        this.renderer.setAttribute(element, "href", url);
    }

    public setupPageMetaTags(page: Page) {
        this.subdomain.getClient().subscribe((subdomain: Client) => {
            const pageTitle = page?.additionalTitles?.[this.locale] || page?.title;
            const formattedTitle = `${subdomain?.displayName || subdomain?.name || "Voyse"} - ${pageTitle}`;
            this.title.setTitle(formattedTitle);
			this.updateCanonicalUrl(page.permalink);

            const description = this.getDefaultDescription(
                page?.additionalMetadata?.[this.locale]?.metaDescription || page?.metaDescription,
                subdomain
            );

            this.meta.addTags([
                { name: "title", property: "og:title", content: formattedTitle },
                { name: "description", property: "description", content: description },
                { property: "og:type", content: "website" },
                { property: "og:description", content: description },
                { property: "og:url", content: page.permalink },
                { property: "article:published_time", content: page.createdAt.toString() },
                { property: "article:modified_time", content: page.updatedAt.toString() },
            ]);

            if (subdomain.integrations?.searchconsole?.apiKey) {
                this.meta.addTags([
                    { name: "google-site-verification", content: subdomain.integrations.searchconsole.apiKey },
                ]);
            }

            page.languages?.forEach((language: string) => {
                this.addHrefLangTags(page, language, subdomain);
            });

            this.meta.addTags(this.getPageImageMetaTags(page, subdomain));
        });
    }

    private addHrefLangTags(page: Page, lang: string, client: Client) {
        if (lang) {
            const linkElt = this.renderer.createElement("link");
            this.renderer.setAttribute(linkElt, "rel", "alternate");
            this.renderer.setAttribute(linkElt, "hreflang", lang);

            const url = this.generateUrl(client, lang, page.defaultPage ? "" : page.slug);
            this.renderer.setAttribute(linkElt, "href", url);
            this.renderer.appendChild(this.document.head, linkElt);
        }
    }

    private addJobHrefLangTag(job: Job, lang: string, client: Client) {
        if (lang) {
            const linkElt = this.renderer.createElement("link");
            this.renderer.setAttribute(linkElt, "rel", "alternate");
            this.renderer.setAttribute(linkElt, "hreflang", lang);

            const url = this.generateUrl(client, lang, `jobs/${job.slug}`);
            this.renderer.setAttribute(linkElt, "href", url);
            this.renderer.appendChild(this.document.head, linkElt);
        }
    }

    private generateUrl(client: Client, lang: string, path: string): string {
        if (client.alias) {
            return `https://${client.alias}/${lang}/${path}`;
        } else {
            return `${environment.domain.replace("foo", client.slug)}/${lang}/${path}`;
        }
    }

    private getJobLocationFromAddressComponents(job: Job): string {
        if (!job.locationMeta?.address_components) {
            return job.location || '';
        }

        const addressComponents = job.locationMeta.address_components;

        // Try to get city/locality first
        const city = addressComponents.find(component =>
            component.types.includes('locality')
        );

        // If no city, try administrative_area_level_1 (state/province)
        const state = addressComponents.find(component =>
            component.types.includes('administrative_area_level_1')
        );

        // If no state, try country
        const country = addressComponents.find(component =>
            component.types.includes('country')
        );

        // Return the most specific location available
        if (city?.long_name) {
            return city.long_name;
        } else if (state?.long_name) {
            return state.long_name;
        } else if (country?.long_name) {
            return country.long_name;
        }

        return job.location || '';
    }

    private formatJobTitle(job: Job, client: Client): string {
        const location = this.getJobLocationFromAddressComponents(job);
        const clientName = client?.displayName || client?.name || "Voyse";

        if (location) {
            return `${job.title}, ${location} - ${clientName}`;
        } else {
            return `${job.title} - ${clientName}`;
        }
    }

    private generateCloudinaryImageUrl(publicId: string, format: string = 'jpg', width: number = this.DEFAULT_IMAGE_WIDTH, height: number = this.DEFAULT_IMAGE_HEIGHT): string {
        return `${this.CLOUDINARY_BASE_URL}/c_fit,h_${height},w_${width}/v1/${publicId}.${format}`;
    }

    private getJobImageMetaTags(job: Job, subdomain: Client): any[] {
        if (job?.metaImage?.public_id) {
            const imageUrl = this.generateCloudinaryImageUrl(job.metaImage.public_id, job.metaImage.format);
            return [
                { type: "image", property: "og:image", content: imageUrl },
                { property: "og:image:secure_url", content: imageUrl },
                { property: "og:image:width", content: this.DEFAULT_IMAGE_WIDTH.toString() },
                { property: "og:image:height", content: this.DEFAULT_IMAGE_HEIGHT.toString() },
            ];
        } else if (subdomain?.jobsMetaImage?.public_id) {
            const imageUrl = this.generateCloudinaryImageUrl(
                subdomain.jobsMetaImage.public_id,
                subdomain.jobsMetaImage?.format,
                this.LARGE_IMAGE_WIDTH,
                this.LARGE_IMAGE_HEIGHT
            );
            return [
                { type: "image", property: "og:image", content: imageUrl },
                { property: "og:image:secure_url", content: imageUrl },
                { property: "og:image:width", content: this.LARGE_IMAGE_WIDTH.toString() },
                { property: "og:image:height", content: this.LARGE_IMAGE_HEIGHT.toString() },
            ];
        } else if (job.video && job.videoData) {
            const thumbnail = job.videoData?.customThumbnail?.public_id
                ? this.generateCloudinaryImageUrl(
                    job.videoData.customThumbnail.public_id,
                    job.videoData.customThumbnail.format
                )
                : job.videoData?.thumbnail;
            return [
                { type: "image", property: "og:image", content: thumbnail },
                { property: "og:image:secure_url", content: thumbnail },
                { property: "og:image:width", content: this.DEFAULT_IMAGE_WIDTH.toString() },
                { property: "og:image:height", content: this.DEFAULT_IMAGE_HEIGHT.toString() },
            ];
        } else {
            const defaultImageUrl = this.generateCloudinaryImageUrl(this.DEFAULT_IMAGE_PATH);
            return [
                { type: "image", property: "og:image", content: defaultImageUrl },
                { property: "og:image:secure_url", content: defaultImageUrl },
                { property: "og:image:width", content: this.DEFAULT_IMAGE_WIDTH.toString() },
                { property: "og:image:height", content: this.DEFAULT_IMAGE_HEIGHT.toString() },
            ];
        }
    }

    private getPageImageMetaTags(page: Page, subdomain: Client): any[] {
        if (page.metaImage?.public_id) {
            const imageUrl = this.generateCloudinaryImageUrl(page.metaImage.public_id, page.metaImage?.format);
            return [
                { type: "image", property: "og:image", content: imageUrl },
                { property: "og:image:secure_url", content: imageUrl },
                { property: "og:image:width", content: this.DEFAULT_IMAGE_WIDTH.toString() },
                { property: "og:image:height", content: this.DEFAULT_IMAGE_HEIGHT.toString() },
            ];
        } else if (subdomain?.jobsMetaImage?.public_id) {
            const imageUrl = this.generateCloudinaryImageUrl(
                subdomain.jobsMetaImage.public_id,
                subdomain.jobsMetaImage?.format,
                this.LARGE_IMAGE_WIDTH,
                this.LARGE_IMAGE_HEIGHT
            );
            return [
                { type: "image", property: "og:image", content: imageUrl },
                { property: "og:image:secure_url", content: imageUrl },
                { property: "og:image:width", content: this.LARGE_IMAGE_WIDTH.toString() },
                { property: "og:image:height", content: this.LARGE_IMAGE_HEIGHT.toString() },
            ];
        } else {
            const defaultImageUrl = this.generateCloudinaryImageUrl(this.DEFAULT_IMAGE_PATH);
            return [
                { type: "image", property: "og:image", content: defaultImageUrl },
                { property: "og:image:secure_url", content: defaultImageUrl },
                { property: "og:image:width", content: this.DEFAULT_IMAGE_WIDTH.toString() },
                { property: "og:image:height", content: this.DEFAULT_IMAGE_HEIGHT.toString() },
            ];
        }
    }

    private getDefaultDescription(content: string, subdomain: Client): string {
        return content || subdomain?.metaText || `Learn more about working for ${subdomain?.displayName || subdomain?.name} here. Powered by Voyse.`;
    }

    public tearDownMetaTags() {
        this.meta.removeTag("property='og:title'");
        this.meta.removeTag("property='og:type'");
        this.meta.removeTag("property='og:site_name'");
        this.meta.removeTag("property='og:url'");
        this.meta.removeTag("property='og:video:url'");
        this.meta.removeTag("property='og:video:secure_url'");
        this.meta.removeTag("property='og:video:type'");
        this.meta.removeTag("property='og:description'");
        this.meta.removeTag("property='article:published_time'");
        this.meta.removeTag("property='article:modified_time'");
        this.meta.removeTag("property='og:image'");
        this.meta.removeTag("property='og:image:secure_url'");
        this.meta.removeTag("property='og:image:width'");
        this.meta.removeTag("property='og:image:height'");
        this.meta.removeTag("name='google-site-verification'");
    }
}
